/**
 * Dealer authentication persistence helper.
 *
 * Design goals (Phase 7 hotfix):
 *  - Persist the JWT + minimal dealer profile in localStorage so the session
 *    survives page refresh, new tab, and browser restart. Session ends ONLY
 *    when the dealer explicitly logs out or the backend rejects the token
 *    (`GET /api/dealer/me` → 401).
 *  - Provide ONE authoritative reader (`getDealerSession`) so every component
 *    (CarDetail, DealerDashboard, DealerBidPanel) agrees on whether a dealer
 *    is logged in — this replaces the buggy mixed sessionStorage/localStorage
 *    reads that made `isDealer` permanently false on car pages.
 *  - Emit a same-tab custom event on set/clear so React components can react
 *    to login/logout without a full page reload.
 */

const TOKEN_KEY = "dealer_token";
const INFO_KEY = "dealer_info";
const EVENT_NAME = "dealer-auth-change";

export function getDealerToken() {
  try {
    return localStorage.getItem(TOKEN_KEY) || null;
  } catch {
    return null;
  }
}

export function getDealerInfo() {
  try {
    const raw = localStorage.getItem(INFO_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function getDealerSession() {
  return { token: getDealerToken(), dealer: getDealerInfo() };
}

export function isDealerLoggedIn() {
  return Boolean(getDealerToken() && getDealerInfo());
}

export function setDealerSession(token, dealer) {
  try {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(INFO_KEY, JSON.stringify(dealer));
    // Broadcast to same-tab listeners (localStorage's `storage` event fires
    // only for OTHER tabs, not the tab that wrote).
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { token, dealer } }));
  } catch {
    /* storage quota / private mode — ignore */
  }
}

export function updateDealerInfo(dealer) {
  try {
    localStorage.setItem(INFO_KEY, JSON.stringify(dealer));
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { dealer } }));
  } catch {
    /* ignore */
  }
}

export function clearDealerSession() {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(INFO_KEY);
    // ALSO clear legacy sessionStorage keys used before this Phase 7 hotfix,
    // so no stale-tab session lingers after upgrade.
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(INFO_KEY);
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { token: null, dealer: null } }));
  } catch {
    /* ignore */
  }
}

export const DEALER_AUTH_EVENT = EVENT_NAME;
